import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Empty, Field, Td, Th } from "../common/UI";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

function Input({ f, value, onChange }) {
  const tid = `config-field-${f.name}`;
  if (f.type === "bool") return <input type="checkbox" data-testid={tid} checked={!!value} onChange={(e) => onChange(e.target.checked)} />;
  if (f.type === "textarea") return <textarea rows={f.rows || 3} data-testid={tid} className="otms-input font-mono text-xs" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "select")
    return (
      <select data-testid={tid} className="otms-input" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        {!f.required && <option value="">—</option>}
        {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  return <input data-testid={tid} type={f.type || "text"} className="otms-input" value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />;
}

export function ConfigTable({ endpoint, title, columns, fields, editPerm, defaults = {}, testId, note }) {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get(endpoint).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [endpoint]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    const body = {};
    fields.forEach((f) => { const v = edit[f.name]; body[f.name] = v === "" || v === undefined ? (f.type === "bool" ? false : null) : v; });
    try {
      edit.id ? await api.put(`${endpoint}/${edit.id}`, body) : await api.post(endpoint, body);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <section className="rounded-md border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <div><h3 className="text-sm font-semibold text-slate-800">{title}</h3>{note && <p className="text-[11px] text-slate-500">{note}</p>}</div>
        {can(editPerm) && <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => setEdit({ ...defaults })} data-testid={`${testId}-add-button`}><Plus className="mr-1 h-4 w-4" />Add</Button>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead><tr>{columns.map((c) => <Th key={c.key}>{c.label}</Th>)}<Th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                {columns.map((c) => <Td key={c.key}>{c.render ? c.render(r) : String(r[c.key] ?? "—")}</Td>)}
                <Td>{can(editPerm) && <Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`${testId}-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}</Td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <Empty />}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle>{edit?.id ? "Edit" : "Add"} — {title}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-3 md:grid-cols-2">
              {fields.map((f) => (
                <Field key={f.name} label={`${f.label}${f.required ? " *" : ""}`} hint={f.hint} className={f.type === "textarea" || f.wide ? "md:col-span-2" : ""}>
                  <Input f={f} value={edit[f.name]} onChange={(v) => setEdit({ ...edit, [f.name]: v })} />
                </Field>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid={`${testId}-save-button`}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
